<template>
  <div>
    <div>
      <!-- item list -->
      <div class="grid mb-8 lg:mb-24 theme theme-white animated-cards">
        <div
          v-if="!loading && items.length === 0"
          class="max-w-1440 mx-auto px-4 md:px-8 lg:px-16 py-8 w-full"
          v-html="noResultText"
        />
        <template v-else>
          <template v-if="groupByCategories">
            <div
              v-for="(group, index) in itemsByCategory"
              :key="group.name"
            >
              <h2 class="max-w-1440 mx-auto px-4 md:px-8 lg:px-16 py-8 w-full text-3xl">
                {{ group.category.name }}
              </h2>
              <div class="grid gap-6 grid-cols-2 md:grid-cols-4 max-w-1440 mx-auto px-4 md:px-8 lg:px-16">
                <product-card
                  v-for="item in group.items"
                  :key="item.id"
                  :model="item"
                  class="mb-8"
                  width="300"
                  height="300"
                  @item-click="onItemClick"
                />
              </div>
              <div
                v-if="group.category.categoryLink"
                class="max-w-1440 mx-auto px-4 md:px-8 lg:px-16 py-8"
              >
                <a
                  class="action"
                  :href="group.category.categoryLink.url"
                  :target="group.category.categoryLink.openInNewWindow ? '_blank' : '_self'"
                  @click="onCategoryLinkClick(group.category.categoryLink.text)"
                >
                  <span>{{ group.category.categoryLink.text }}</span>
                  <span class="icon-arrow-right-new text-3xs ml-2" />
                  <span class="icon-arrow-left text-xs mr-2" />
                </a>
              </div>
              <template
                v-if="shouldRenderInspirationBlock(index) && typeof inspirationBlocks[currentInspirationIndex(index)] !== 'undefined'"
              >
                <search-inspiration-block
                  :inspirations="inspirationBlocks[currentInspirationIndex(index)]"
                />
              </template>
            </div>
          </template>
          <search-items-product-list-with-inspirations
            v-else
            :items="items"
            :inspirations="inspirationBlocks"
            @item-click="onItemClick"
          />
        </template>
      </div>

      <!-- Search more -->
      <div
        v-if="!loading && !allItemsLoaded"
        class="text-center mb-12 theme-white"
      >
        <button
          class="md:hidden btn inline-block"
          @click="onLoadMore"
        >
          {{ loadMoreButtonText }}
        </button>
        <button
          class="hidden md:inline-block btn hover:bg-blue-dark hover:text-white transition-all"
          @click="onLoadMore"
        >
          {{ loadMoreButtonText }}
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="text-center">
      <div class="spinner spinner-lg" />
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import api from '@/api'
import tracking from '@/helpers/tracking'
import searchFactory from '@/compositions/search/SearchFactory'
import searchConfig from '@/compositions/search/SearchConfiguration'
import searchQueryState from '@/compositions/search/SearchQuery'
import SearchInspirationBlock from '@/components/search/SearchInspirationBlock.vue'
import ProductCard from '@/components/products/ProductCard.vue'
import SearchItemsProductListWithInspirations from './SearchItemsProductListWithInspirations.vue'

export default {
  components: {
    ProductCard,
    SearchInspirationBlock,
    SearchItemsProductListWithInspirations
  },
  setup() {
    const placement = 'product overview'
    const { getActiveConfig } = searchConfig.searchConfigurationUse()
    const {
      items, itemsByCategory, totalCount, searchMore, trackId, loading, allItemsLoaded
    } = searchFactory.useSearchResult()
    const { activeTerms, groupByCategories } = searchQueryState.searchQueryUse()

    function onItemClick({ hitId, position }) {
      api.trackItemClick({
        searchType: 'product',
        trackId: trackId.value,
        hitId,
        terms: encodeURIComponent(activeTerms.value),
        position
      })
    }

    const onLoadMore = () => {
      tracking.loadMore(placement)
      searchMore()
    }

    const onCategoryLinkClick = (linkName) => {
      tracking.productDiscoverMore({ linkName, placement })
    }

    const inspirationBlocks = computed(() => getActiveConfig().inspirationBlocks)
    const shouldRenderInspirationBlock = (index) => index >= 1 && index % 2 !== 0
    const currentInspirationIndex = (index) => Math.round((1 / 3) * index)

    return {
      allItemsLoaded,
      loading,
      items,
      itemsByCategory,
      groupByCategories,
      totalCount,
      noResultText: computed(() => getActiveConfig().translations.noResultHtml),
      loadMoreButtonText: computed(() => getActiveConfig().translations.loadMoreButtonText),
      inspirationBlocks,
      shouldRenderInspirationBlock,
      currentInspirationIndex,

      onCategoryLinkClick,
      onLoadMore,
      onItemClick
    }
  }
}
</script>
